import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import Badge from '../../../components/ui/Badge'
import Like from '../../../components/ui/Like'
import TabButton from '../../../components/ui/TabButton'
import QuantitySelector from '../../../components/ui/QuantitySelector'
import BuyActions from './tabs/BuyActions'
import RentActions from './tabs/RentActions'
import SwapActions from './tabs/SwapActions'
import { formatCurrency } from '../../../utils/formatters'
import { calculateShippingRate } from '../../../redux/slices/deliverySlice'
import { setUseEstimator, setSelectedRate } from '../../../redux/slices/deliverySlice'
import FormInput from '../../../components/ui/FormInput'
import { useGetAddressesQuery } from '../../../redux/apis'
import LoadingSpinner from '../../../components/ui/LoadingSpinner'
import { setCurrentProduct } from '../../../redux/slices/productSlice'

const ProductActions = ({ product, onAddToCart, className = '' }) => {
  const dispatch = useDispatch()
  const [activeTab, setActiveTab] = useState(() => {
    if (product?.forSell) return 'buy'
    if (product?.forRent) return 'rent'
    if (product?.openToSwap) return 'swap'
    return 'buy'
  })

  // Store the product in Redux when component loads
  useEffect(() => {
    if (product) {
      dispatch(setCurrentProduct(product));
    }
  }, [product, dispatch]);

  // Shipping states
  const [selectedAddressId, setSelectedAddressId] = useState('')
  const [selectedShippingRate, setSelectedShippingRate] = useState(null)
  const [quantity, setQuantity] = useState(1)

  const handleQuantityChange = (newQuantity) => {
    setQuantity(Math.max(1, Math.min(parseInt(stock), newQuantity)));
  };

  // Get user addresses
  const { data: userAddresses = [], isLoading: isLoadingAddresses } =
    useGetAddressesQuery(undefined, {
      skip: !useSelector(state => state.auth?.isAuthenticated)
    })

  // Get shipping state from Redux
  const shippingRates = useSelector(
    state => state.shipping?.shippingRates || null
  )
  const isCalculatingRates = useSelector(
    state => state.shipping?.loading || false
  )
  const shippingError = useSelector(state => state.shipping?.error || null)

  // Reset selected rate when shipping rates change
  useEffect(() => {
    if (
      shippingRates &&
      Array.isArray(shippingRates) &&
      shippingRates.length > 0
    ) {
      // Default to the cheapest rate
      const cheapestRate = shippingRates.reduce((prev, current) =>
        parseFloat(prev.amount) < parseFloat(current.amount) ? prev : current
      )
      setSelectedShippingRate(cheapestRate)
    } else {
      setSelectedShippingRate(null)
    }
  }, [shippingRates])

  if (!product) return null

  const {
    name,
    forSell,
    forRent,
    openToSwap,
    price = 0,
    currency = 'USD',
    stock = 0,
    productType,
    shippingAddress,
    pickupAddress,
    useShippingForPickup,
    parcelInfo,
    openToShipping,
    openToPickUp
  } = product

  const isVirtual = productType === 'virtual'

  // Determine which address to use for shipping origin
  const originAddress = openToShipping
    ? shippingAddress || (useShippingForPickup ? pickupAddress : null)
    : null

  // Handle shipping address selection - now simplified to just trigger estimator
  const handleAddressSelect = address => {
    // Force estimator mode for shipping
    dispatch(setUseEstimator(true));
    
    // If address has forceEstimate flag, that means we're using simplified flow
    if (address && address.forceEstimate) {
      console.log('Using simplified shipping estimator instead of real rates');
      // Create an estimated rate
      const estimatedRate = {
        provider: 'Estimated',
        serviceName: 'Standard',
        amount: '5.99',
        currency: 'USD',
        days: '1-3',
        isEstimate: true
      };
      dispatch(setSelectedRate(estimatedRate));
    }
  };

  // Handle quantity change
  // const handleQuantityChange = (newQuantity) => {
  //   setQuantity(newQuantity);
  // };

  // Group badges by type
  const typeBadges = [
    productType === 'designer' && 'designer',
    productType === 'virtual' && 'virtual'
  ].filter(Boolean)

  return (
    <div className={`space-y-8 ${className}`}>
      {/* Name, Badges, and Like Button */}
      <div className='space-y-3'>
        <div className='flex items-start justify-between'>
          <h1 className='text-2xl font-bold text-gray-900 sm:text-3xl'>
            {name}
          </h1>
          <Like productId={product.id} size='md' showCount={true}/>
        </div>
        {typeBadges.length > 0 && (
          <div className='flex flex-wrap gap-2'>
            {typeBadges.map(type => (
              <Badge key={type} type={type} />
            ))}
          </div>
        )}
      </div>

      {/* Transaction Type Tabs */}
      <div className='border-b border-gray-200'>
        <nav className='-mb-px flex space-x-8'>
          {forSell && (
            <TabButton
              onClick={() => setActiveTab('buy')}
              isActive={activeTab === 'buy'}
            >
              Buy
            </TabButton>
          )}
          {forRent && (
            <TabButton
              onClick={() => setActiveTab('rent')}
              isActive={activeTab === 'rent'}
            >
              Rent
            </TabButton>
          )}
          {openToSwap && (
            <TabButton
              onClick={() => setActiveTab('swap')}
              isActive={activeTab === 'swap'}
            >
              Swap
            </TabButton>
          )}
        </nav>
      </div>

      {/* Price Display */}
      <div className='space-y-2'>
        {forSell && activeTab === 'buy' && (
          <div className='flex items-baseline justify-between'>
            <div className='flex items-baseline gap-2'>
              <span className='text-2xl font-bold text-gray-900'>
                {formatCurrency(price, currency)}
              </span>
            </div>
          </div>
        )}
      </div>
      
      {/* Quantity Selector - Show only for physical products and buy tab */}
      {!isVirtual &&  (
        <QuantitySelector
          value={quantity}
          onChange={handleQuantityChange}
          stock={stock}
          min={1}
          max={10} // Optional max limit regardless of stock
          showAvailable={true}
          disabled={stock <= 0}
        />
      )}

      {/* Shipping Calculator - Show only for physical products with shipping */}
      {!isVirtual && (openToShipping || openToPickUp) && (
        <div className='mt-2 border-gray-200'>
         
          {/* If we have shipping or pickup available, show the options */}
          {activeTab === 'buy' && (
            <BuyActions
              product={product}
              onAddToCart={onAddToCart}
              shippingRate={selectedShippingRate}
              onAddressSelect={handleAddressSelect}
            />
          )}

          {activeTab === 'rent' && (
            <RentActions
              product={product}
              onAddToCart={onAddToCart}
            />
          )}

          {activeTab === 'swap' && (
            <SwapActions
              product={product}
              onAddToCart={onAddToCart}
            />
          )}
        </div>
      )}

      {/* Alert for expensive items */}
      {!isVirtual && price >= 1000 && (
        <div className="mt-6 bg-yellow-50 p-4 rounded-md">
          <div className="flex">
            <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Secure Purchase</h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>For high-value purchases, we offer additional verification and insurance options at checkout.</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProductActions
